import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, concatMap, delayWhen, mergeMap, retry, retryWhen, timeout } from 'rxjs/operators';
import { AuthStateModel } from 'src/app/store/auth/auth.model';
import { AuthState } from 'src/app/store/auth/auth.state';
import { Logout, RefreshToken } from '../store/auth.actions';
import { API_URL, HTTP_TIMEOUT } from './app.constants';
import { AuthService } from './auth/auth.service';
import { originalError, originalRequest } from './error-monitoring.service';

// Each status code can retry with a different delay.
// 504 is omitted on purpose: gateway/Mongo timeouts are not transient, and
// retrying them re-issues the same expensive query (e.g. stats/task-total).
const retryStatusCodes: Record<number, number> = {
  0: 1000,
  429: 1000,
  500: 1000,
  502: 1000,
  503: 1000,
  511: 1000,
};
const retryCount = 1;

/** Milliseconds to wait before retrying, or null to fail the request immediately. */
export function getRetryDelay(status: number, attempt: number): number | null {
  if (attempt >= retryCount) return null;
  const delayTime = retryStatusCodes[status];
  return delayTime > 0 ? delayTime : null;
}

const refreshableErrors = ['expiredAccessToken', 'invalidToken', 'Unauthorized', 'Access or user validation failed'];

const expiredErrors = new Set(['invalidCredentials', 'expiredRefreshToken']);

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  constructor(
    private store: Store,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private authService: AuthService,
    @Inject(HTTP_TIMEOUT) private httpTimeout: number,
    @Inject(API_URL) private apiUrl: string,
  ) {
    this.apiUrl = this.apiUrl.replace('{version}', '');
  }

  openSnackBar(message: string, action: string, duration = 2000) {
    this.snackBar.open(message, action, {
      duration,
    });
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // TODO: add headers only for own API urls
    if (!req.url.startsWith('/')) {
      const auth: AuthStateModel = this.store.selectSnapshot(AuthState);
      let params = req.params;

      const companyId = auth?.company?.id;
      if (companyId && !params.has('company')) { params = params.set('company', companyId); }
      if (!params.get('company')) { params = params.delete('company'); }

      const token = auth?.token;
      const cookieAuth = auth?.cookieAuth || this.authService.version === 'v2';
      const setHeaders = {};
      if (!req.headers.has('Authorization')) {
        if (token) {
          Object.assign(setHeaders, this.authService.getTokenHeader(token));
        }
      }

      const withCredentials = req.withCredentials || (cookieAuth && req.url.startsWith(this.apiUrl));

      req = req.clone({ params, setHeaders, withCredentials });
    }

    const isRefresh = req.url.includes('auth/token/refresh');

    const httpRequest = next.handle(req).pipe(
      timeout(this.httpTimeout),
      retry({
        delay: (error, count) => {
          if (count <= 1 && refreshableErrors.includes(error?.error?.error) && !isRefresh) {
            return this.store.dispatch(new RefreshToken());
          }

          return throwError(() => error);
        },
      }),
      retryWhen(errors => errors
        .pipe(
          concatMap((error, count) => {
            const delayTime = getRetryDelay(error.status, count);
            if (delayTime != null) {
              return of(delayTime);
            }

            return throwError(() => error);
          }),
          delayWhen(time => timer(time)),
        ),
      ),
      catchError((err) => {
        if (err instanceof HttpErrorResponse) {
          const res = err.error || {};
          if (typeof res === 'object') {
            res[originalError] = err;
            res[originalRequest] = req;
            if (res?.error === 'ssoOnlyAuth') {
              this.store.dispatch(new Logout(true));
              this.openSnackBar(this.translate.instant('login.ssoOnlyAuth'), null);
            } else if (res?.error === 'loginWithoutProvisioning') {
              this.store.dispatch(new Logout(true));
              this.openSnackBar(this.translate.instant('login.loginWithoutProvisioning'), null, 5000);
            } else if (expiredErrors.has(res?.error)) {
              this.store.dispatch(new Logout(false, 'login.tokenExpired'));
            }
          }
          return throwError(() => res);
        }
        return throwError(() => err);
      }),
      mergeMap((event) => {
        if (event instanceof HttpResponse) {
          if (event.body?.error) {
            const res = event.body || {};
            if (typeof res === 'object') {
              res[originalError] = event;
              res[originalRequest] = req;
            }
            return throwError(() => res);
          }
        }
        return of(event);
      }),
    );
    return httpRequest;
  }
}

export const httpInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: HttpInterceptorService,
  multi: true,
};
